import { Component, ElementRef, NgZone, OnInit, ViewContainerRef } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { DataSharingLTE } from '@fs/services/data-sharing-lte';
import { FormInit } from '@fs/services/form-init';
import { CommonMethods } from '@services/common.method';
import { customJS } from '@fs/services/customjs';
import { TabOpenService } from '@services/tab-open.service';
import { TabParams } from '@fs/services/tab-params.service';
import { SaItemService } from '@fs/services/price-book/sa-item/sa-item.service';
import { Constants } from '@constants';
import { MultiSelectModel } from '@fs/models/equipment.model';
import { JobsiteEquipmentService } from '@fs/services/jobsite-equipment';
import { DataSourceRequestState } from '@progress/kendo-data-query';
import { concat, forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FsUtil } from '@fs/services/fs-utils';
import { AgreementRequestModel } from '@fs/models/agreement/request.model';
import { AgreementService } from '@fs/services/agreement/agreement.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { NotificationsService } from "@shared/services/notification";
import { AgreementVisitModel } from '@fs/models/agreement/visit.model';
import { AgreementResponseModel } from '@fs/models/agreement/response.model';
import { DatePipe } from '@angular/common';
import { WorkOrderModel, WorkTicketPriceBookItemEquipment } from '@fs/models/work-order/work-order.model';

@Component({
  selector: 'agreement-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss'],
  providers: [FormInit, AgreementService, DatePipe]
})
export class SaCreateComponent implements OnInit {
  agreementForm: FormGroup;
  visitsForm = new FormArray([]);
  data: AgreementResponseModel;
  equipmentData: MultiSelectModel[] = [];
  state: DataSourceRequestState = {
    skip: 0,
    take: -1,
    sort: [],
  }
  selectedEquipments: WorkTicketPriceBookItemEquipment[] = [];
  focusedDate: Date = new Date();
  tabCounter: number;
  isUnsavedTriggered: boolean = false;
  overlay: boolean = false;
  loading: boolean = true;
  isAddMode: boolean;

  private set inputParams(val)  {
    this.data = val;
    this.isAddMode = this.data.ID == "";
  }; 
  private readonly ngUnsubscribe = new Subject();

  constructor(private dataShareEmitter: DataSharingLTE, private formInit: FormInit, private commonMethods: CommonMethods, 
              private zone: NgZone, private tabOpenServ: TabOpenService, private agreementItemServ: SaItemService,
              private equipmentServ: JobsiteEquipmentService, private agreementServ: AgreementService, private toastServ: NotificationsService,
              private spinnerServ: SpinnerService, private $viewContainerRef: ViewContainerRef, private $elementRef: ElementRef,
              private datePipe: DatePipe) { }

  ngOnInit() {    
    this.agreementForm = this.formInit.ServiceAgreementForm(this.data);
    if(this.isAddMode) this.setNewAgreement(); else this.getAgreement();   
    this.setOnInitChanges();
    this.tabOpenServ.routeToParentEvent
      .pipe(takeUntil(this.ngUnsubscribe)).subscribe((response:any) => {
          if (
              response.tabCounter == this.tabCounter &&
              response.event.split("|")[0] == "replaceTab" &&
              response.event.split("|")[1] == "discard"
          ) {
              this.tabOpenServ.ReplaceTab({
                  CurrentTabIndex: this.tabCounter,
                  newTabInfo: TabParams.ViewServiceAgreement(this.data)
              }, false);
          } else if (
              response.tabCounter == this.tabCounter &&
              response.event.split("|")[0] == "replaceTab" &&
              response.event.split("|")[1] == "save"
          ) {
              this.onSubmitAgreement();
          }
      });
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  setOnInitChanges() {
    this.tabOpenServ.ConfirmDestroyTabEvent.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(r => { if (r.tabCounter == this.tabCounter) { this.onSubmitAgreement(); }
    });
    this.agreementForm.valueChanges.subscribe(() => {
      this.formDirtyDetection();
    });
    this.visitsForm.valueChanges.subscribe(() => {
      this.formDirtyDetection();
    });
  }
  formDirtyDetection() {
    if (!this.isUnsavedTriggered) {
      if (this.agreementForm.dirty || this.visitsForm.dirty) {
        this.isUnsavedTriggered = true;
        this.tabOpenServ.unsavedChangedDetected({
          dirty: true,
          Index: this.tabCounter
        });
      }
    }
  }
  onSubmitAgreement() {
    if (this.agreementForm.valid && this.visitsForm.valid) {
      let formValues = this.agreementForm.getRawValue();
      let visitFormArray = this.visitsForm.getRawValue();
      let model = new AgreementRequestModel();

      CommonMethods.ObjectMapper(formValues, model);
      model.EffectiveDate = this.datePipe.transform(model.EffectiveDate, "MM/dd/yyyy");
      model.ExpirationDate = this.datePipe.transform(model.ExpirationDate, "MM/dd/yyyy");
      model.EquipmentIDs = this.selectedEquipments.map(x => x.JobSiteEquipment.ID);
      model.OriginWorkTicketPriceBookItemID = this.data.OriginWorkTicketPriceBookItemID;
      visitFormArray.forEach(x => {
        let visitModel = new AgreementVisitModel();
        CommonMethods.ObjectMapper(x, visitModel);
        visitModel.ProposedDate = this.datePipe.transform(visitModel.ProposedDate, "MM/dd/yyyy");
        if (!FsUtil.checkNullOrUndefined([visitModel.FollowupDate], true)) {
          visitModel.FollowupDate = this.datePipe.transform(visitModel.FollowupDate, "MM/dd/yyyy");
        }
        model.Visits.push(visitModel);
      });
      model.ModifyDate = this.data.ModifyDate;

      let http = (this.isAddMode) ? this.agreementServ.PostAgreement(this.data.OriginatingWorkticket.JobSite.ID, model)
                                  : this.agreementServ.PutAgreement(this.data.OriginatingWorkticket.JobSite.ID, model);
      this.spinnerServ.showloader(this.$viewContainerRef, this.tabCounter, 0);
      concat(http).pipe(takeUntil(this.ngUnsubscribe)).subscribe(r => {
        this.zone.runOutsideAngular(() => {
          customJS.markTabAsPristine(this.$elementRef.nativeElement);
        });
        this.dataShareEmitter.emitWoReviewRefresh({ ID: this.data.OriginatingWorkticket.ID, 
                                                    WtPriceBookItemID: this.data.OriginWorkTicketPriceBookItemID, 
                                                    ServiceAgreement: { ID: r.ID, AgreementID: r.ServiceAgreementNumber } 
                                                  });
        this.spinnerServ.hideLoader(this.$viewContainerRef, this.tabCounter);
        let message = "Agreement " + r.ServiceAgreementNumber + " ";
        message += (this.isAddMode) ? "created successfully" : "updated successfully";
        this.toastServ.success(message, "Success");
        this.tabOpenServ.ReplaceTab({ CurrentTabIndex: this.tabCounter,
                                      newTabInfo: TabParams.ViewServiceAgreement(r)
                                    }, false);
        this.zone.runOutsideAngular(() => {
          customJS.scrollToPageHeader();
        });
      }, (e) => {
        this.spinnerServ.hideLoader(this.$viewContainerRef, this.tabCounter);
        if (!CommonMethods.CheckNullOrUndefined(e.error.Message))
            this.toastServ.showMultiLineError(e.error.Message, "Error");
      });
    } else {
      this.commonMethods.markFormGroupTouched(this.agreementForm);
      this.commonMethods.markFormArrayTouched(this.visitsForm);
      this.zone.runOutsideAngular(() => {
        customJS.verifyBoxes(this.$elementRef.nativeElement);
      });
    }
  }
  openWorkOrderTab() {
    this.tabOpenServ.OpenTab(TabParams.WorkOrderReviewView(this.data.OriginatingWorkticket));
  }
  openAgreementItemTab() {
    this.tabOpenServ.OpenTab(TabParams.AgreementItemView(this.data.ServiceAgreementItem.ID, this.data.ServiceAgreementItem.ItemNumber));
  }
  equipmentChanged(e) {
    this.agreementForm.markAsDirty();
    this.formDirtyDetection();
  }
  addVisit(e){
    e.stopPropagation();
    this.visitsForm.push(this.formInit.VisitForm(new AgreementVisitModel()));
    setTimeout(() => {
      $(".main__content_section").animate({ scrollTop: $(".main__content_section")[0].scrollHeight }, 1000);
    }, 5);
  }
  removeVisit(e) {
    this.visitsForm.removeAt(e);
  }
  checkFirstVisit(val: boolean) {
    if(this.visitsForm.controls.length > 0) {
      let firstVisitControl = this.visitsForm.controls.find(x => this.data.OriginatingWorkticket && x.get("WorkTicketID").value == this.data.OriginatingWorkticket.ID);
       
      if (!firstVisitControl)
        firstVisitControl = this.visitsForm.controls[0];

      if (!firstVisitControl.get("IsAdhoc").value) {
        if (val) {
          firstVisitControl.get("WorkTicketID").setValue(this.data.OriginatingWorkticket.ID);
          firstVisitControl.get("WoNumber").setValue(this.data.OriginatingWorkticket.WorkTicketNo);
          firstVisitControl.get("PromisedDate").setValue(this.data.OriginatingWorkticket.PromisedDate);
          firstVisitControl.get("ServiceTypeID").setValue(this.data.OriginatingWorkticket.ServiceType.ID);
          firstVisitControl.get("ServiceType").setValue(this.data.OriginatingWorkticket.ServiceType.Name);
          firstVisitControl.get("ServiceTypeID").disable();
          firstVisitControl.get("ProposedDate").setValue("");
          firstVisitControl.get("ProposedDate").disable();
          firstVisitControl.get("FollowupDate").setValue("");
          firstVisitControl.get("FollowupDate").disable();
          firstVisitControl.get("ProposedDate").disable();
        } else {
          firstVisitControl.get("WorkTicketID").setValue("");
          firstVisitControl.get("WoNumber").setValue("");
          firstVisitControl.get("PromisedDate").setValue("");
          firstVisitControl.get("ProposedDate").enable(); 
          firstVisitControl.get("ServiceTypeID").setValue("");
          firstVisitControl.get("ServiceType").setValue("")
          if (firstVisitControl.get('ProposedDate').value instanceof Date)
            firstVisitControl.get("FollowupDate").enable();
        }
      }
    }
  }
  setExpirationDate() {
    if (this.agreementForm.controls['EffectiveDate'].value instanceof Date) {
      let expiryDate = (<Date>this.agreementForm.controls['EffectiveDate'].value).addMonths(this.getLengthOfContract());
      this.agreementForm.controls['ExpirationDate'].setValue(expiryDate.addDays(-1));
      if(this.visitsForm.controls.length > 0) {
        let proposedDate = this.visitsForm.controls[0].get("ProposedDate");
        if (!CommonMethods.CheckNullOrUndefined(proposedDate.value, true) && proposedDate.value instanceof Date) {
          let isValid = (<Date>this.agreementForm.controls["EffectiveDate"].value).isLessThanOrEqual(proposedDate.value);
          proposedDate.setErrors(!(isValid) ? { startDateError : true } : null);
        }
      }
    }
  }
  cancelAction() {
    if (!this.isAddMode) {
      this.tabOpenServ.ReplaceTab({ CurrentTabIndex: this.tabCounter,
        newTabInfo: TabParams.ViewServiceAgreement(this.data)
      }, true);
    } else {
      this.tabOpenServ.DestroyTab(this.tabCounter);
    }
  }
  //#region private methods 
  private setNewAgreement() {
    this.agreementForm.controls['EffectiveDate'].setValue(new Date(this.data.OriginatingWorkticket.CompletedDate));
    this.selectedEquipments = this.data.ServiceAgreementEquipments.map(x => new WorkTicketPriceBookItemEquipment(x.ID));
    let agreementItem = this.agreementItemServ.GetSaItem(this.data.ServiceAgreementItem.ID);
    let jobsiteEquipments = this.equipmentServ.GetEquipments({ 
      customerId: this.data.OriginatingWorkticket.Customer.ID, 
      jobsiteId: this.data.OriginatingWorkticket.JobSite.ID 
    }, this.state);
    this.setAddress();
    this.overlay = true;
    this.loading = false;

    forkJoin([agreementItem, jobsiteEquipments]).pipe(takeUntil(this.ngUnsubscribe)).subscribe(res => {
      let note = this.data.ServiceAgreementItem.Note;
      this.overlay = false;
      this.data.ServiceAgreementItem = res[0];
      this.data.ServiceAgreementItem.Note = note;
      this.data.ItemDiscount = res[0].Discount;      
      this.equipmentData = res[1].Data;
      this.generateIntervalVisits();
      this.setExpirationDate();
    });
  }
  private generateIntervalVisits() {
    let interval = this.data.ServiceAgreementItem.IntervalType;
    if (interval != null) {
      let totalVisits = Math.floor((this.getLengthOfContract() * FsUtil.getVisitsPerYear(interval.Code))/12);
      for(let i=0; i < totalVisits; i++) {
        this.visitsForm.push(this.formInit.VisitForm(new AgreementVisitModel(false)));
      }
    }
  }
  private getLengthOfContract() {
    let lengthOfContract = this.data.ServiceAgreementItem.Length;
    let durationType = this.data.ServiceAgreementItem.DurationType;
    if (durationType.Code == 'years') {
      lengthOfContract = lengthOfContract * 12;
    }
    return lengthOfContract;
  }
  private setAddress() {
    if (!CommonMethods.CheckNullOrUndefined(this.data.OriginatingWorkticket.JobSite.Unit, true)) 
    this.data.OriginatingWorkticket.JobSite.StreetNo += ", " + this.data.OriginatingWorkticket.JobSite.Unit;
        
    if (!CommonMethods.CheckNullOrUndefined(this.data.OriginatingWorkticket.Customer.Unit, true))
    this.data.OriginatingWorkticket.Customer.StreetNo += ", " + this.data.OriginatingWorkticket.Customer.Unit;    
  }
  private getAgreement() {
    let agreement = this.agreementServ.GetAgreement(this.data.JobSite.ID, this.data.ID);
    let jobsiteEquipments = this.equipmentServ.GetEquipments({ 
      customerId: this.data.Customer.ID, 
      jobsiteId: this.data.JobSite.ID 
    }, this.state);
    
    this.spinnerServ.showloader(this.$viewContainerRef, this.tabCounter);
    forkJoin(agreement, jobsiteEquipments).pipe(takeUntil(this.ngUnsubscribe)).subscribe(res => {
      this.loading = false;
      this.selectedEquipments = res[0].ServiceAgreementEquipments.map(x => new WorkTicketPriceBookItemEquipment(x.ID));
      this.spinnerServ.hideLoader(this.$viewContainerRef, this.tabCounter);
      this.data = res[0];
      this.equipmentData = res[1].Data;
      if (!this.data.OriginatingWorkticket)
        this.data.OriginatingWorkticket = new WorkOrderModel();
      this.data.OriginatingWorkticket.Customer = Object.assign({}, res[0].Customer);
      this.data.OriginatingWorkticket.JobSite = Object.assign({}, res[0].JobSite);
      this.setAddress();
      this.data.Visits.forEach(x => {
        this.visitsForm.push(this.formInit.VisitForm(x))
      });
      this.agreementForm = this.formInit.ServiceAgreementForm(this.data);
      this.setFirstVisit();
    }, error => {
      this.loading = false;
      this.spinnerServ.hideLoader(this.$viewContainerRef, this.tabCounter);
      if (!FsUtil.checkNullOrUndefined([error.error.Message]))
        this.toastServ.showMultiLineError(error.error.Message, "Error");
    });
  }
  private setFirstVisit() {
    let visit = this.data.Visits.find(x => x.IsAdhoc == false && this.data.OriginatingWorkticket 
                  && x.WorkTicketID == this.data.OriginatingWorkticket.ID);
    if (visit != null) {
      setTimeout(() => {
        $("#firstvisit_yes_" + this.tabCounter.toString()).prop('checked', true);
      }, 200);
    }
  }
  //#endregion
}

